import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { DateTime } from "luxon";
import { UtilsService } from "../../shared/services/utils.service";
import { Gateway, GatewayType } from "../models/gateway.model";
import { OrderByDirection, OrderByItem } from "../models/search-result.model";
import { GatewayService } from "../services/gateway.service";
import { AppStore, GatewayTourScenario } from "./app.store";

enum GatewayScreenMode {
  Normal = "normal",
  Loading = "loading",
}

@Injectable({
  providedIn: "root",
})
export class GatewayStore {
  protected utilsService: UtilsService = inject(UtilsService);
  protected gatewayService: GatewayService = inject(GatewayService);
  protected appStore: AppStore = inject(AppStore);

  private readonly error = signal<string | null>(null);
  readonly error$ = this.error.asReadonly();

  private screenMode: GatewayScreenMode = GatewayScreenMode.Normal;

  private readonly forcedRefresh = signal<boolean>(false);

  // ---------------------------
  // --- touring
  // ---------------------------

  readonly touring = {
    welcomeStep: {
      url: "/gateways",
      title: $localize`:@@gateway.touring.welcomeTitle:Welcome to gateways section`,
      text: $localize`:@@gateway.touring.welcomeText:You will be able to check on your different gateways.`,
    },
    exampleGatewayStep: {
      url: "/gateways",
      title: $localize`:@@gateway.touring.exampleGatewayTitle:Gateway information`,
      text: $localize`:@@gateway.touring.exampleGatewayText:The data (first connection, last seen and type) of your gateways are displayed here.`,
    },
  };

  readonly tourSteps$ = computed(() => {
    const tourSteps = [];

    Object.entries(this.touring).forEach(([step, data]) => {
      tourSteps.push(step + "@" + data.url);
    });

    return tourSteps;
  });

  getTourTitle(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.title : "";
  }

  getTourText(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.text : "";
  }

  // ---------------------------
  // --- page header
  // ---------------------------

  readonly headers$ = computed(() => {
    const headers = [];

    headers.push({
      id: "dashboard",
      label: $localize`:@@store.gatewayLabel:Gateways`,
      url: "/gateways",
    });

    return headers;
  });

  // ---------------------------
  // --- navigation
  // ---------------------------

  // Getter status signals for external access
  readonly loading$ = computed(() => {
    // const gatewaysCount = this.gatewaysCount$();
    const gatewaysLoading = this.gatewaysLoading$();

    return gatewaysLoading;
  });

  triggerForcedRefresh(): void {
    this.forcedRefresh.set(true);
  }

  // ---------------------------
  // --- sorting
  // ---------------------------

  private gatewaysOrderByTimer: any;

  private readonly gatewaysOrderBy = signal<string>("label-asc");
  readonly gatewaysOrderBy$ = this.gatewaysOrderBy.asReadonly();

  readonly sortsById$ = computed(() => {
    const sorts: OrderByItem[] = [
      {
        id: "label-asc",
        field: "label",
        direction: OrderByDirection.asc,
        label:
          $localize`:@@global.sort.label:Sorted on label` +
          "\n" +
          $localize`:@@global.sort.aToZ:(A > Z)`,
      },
      {
        id: "label-desc",
        field: "label",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@global.sort.label:Sorted on label` +
          "\n" +
          $localize`:@@global.sort.zToA:(Z > A)`,
      },
      {
        id: "lastseen-asc",
        field: "last_seen",
        direction: OrderByDirection.asc,
        label:
          $localize`:@@global.sort.comm:Sorted on last comm.` +
          "\n" +
          $localize`:@@global.sort.oldToNew:(oldest > newest)`,
      },
      {
        id: "lastseen-desc",
        field: "last_seen",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@global.sort.comm:Sorted on last comm.` +
          "\n" +
          $localize`:@@global.sort.newToOld:(newest > oldest)`,
      },
    ];

    const sortItems = {};
    sorts.map((sort) => {
      sortItems[sort.id] = sort;
    });

    return sortItems;
  });

  readonly sorts$ = computed(() => {
    const sortsById = this.sortsById$();

    return Object.values(sortsById);
  });

  readonly currentSort$ = computed(() => {
    const sortsById = this.sortsById$();
    const orderBy = this.gatewaysOrderBy$();

    if (!orderBy) return null;

    return sortsById[orderBy];
  });

  updateGatewaysOrderBy(newOrderBy: string): void {
    if (newOrderBy !== this.gatewaysOrderBy()) {
      if (this.gatewaysOrderByTimer) {
        clearTimeout(this.gatewaysOrderByTimer);
      }

      this.gatewaysOrderByTimer = setTimeout(() => {
        this.gatewaysOrderBy.set(newOrderBy);
      }, 100);
    }
  }

  // ---------------------------
  // --- filtering
  // ---------------------------

  private gatewaysSearchTimer: any;

  private readonly gatewaysSearch = signal<string>(null);
  readonly gatewaysSearch$ = this.gatewaysSearch.asReadonly();

  updateGatewaysSearch(newSearch: string): void {
    if (newSearch !== this.gatewaysSearch()) {
      if (this.gatewaysSearchTimer) {
        clearTimeout(this.gatewaysSearchTimer);
      }

      this.gatewaysSearchTimer = setTimeout(() => {
        this.gatewaysSearch.set(newSearch);
      }, 100);
    }
  }

  readonly filteredGateways$ = computed(() => {
    const search = this.gatewaysSearch();
    let filteredGateways = this.gateways$();
    const currentSort = this.currentSort$();

    filteredGateways = filteredGateways.map((gateway) => gateway.clone());

    // filter on search
    const fixedSearch =
      typeof search === "string" && search.trim().length > 0
        ? search.trim().toLowerCase()
        : null;
    if (fixedSearch) {
      filteredGateways = filteredGateways.filter(
        (gateway) =>
          gateway.universalLabel?.trim().toLowerCase().indexOf(fixedSearch) >= 0
      );
      if (filteredGateways.length === 0) return filteredGateways;
    }

    // apply order by
    if (currentSort) {
      if (currentSort.field === "label") {
        if (currentSort.direction === OrderByDirection.asc) {
          filteredGateways = filteredGateways.sort((gatewayA, gatewayB) =>
            gatewayA.universalLabel?.localeCompare(gatewayB.universalLabel)
          );
        } else {
          filteredGateways = filteredGateways.sort((gatewayA, gatewayB) =>
            gatewayB.universalLabel?.localeCompare(gatewayA.universalLabel)
          );
        }
      } else if (currentSort.field === "last_seen") {
        // console.log("SORT BY LAST_SEEN", currentSort);
        if (currentSort.direction === OrderByDirection.asc) {
          filteredGateways = filteredGateways.sort(
            (gatewayA, gatewayB) =>
              gatewayB.lastSeenDuration - gatewayA.lastSeenDuration
          );
        } else {
          filteredGateways = filteredGateways.sort(
            (gatewayA, gatewayB) =>
              gatewayA.lastSeenDuration - gatewayB.lastSeenDuration
          );
        }
      }
    }

    // console.log(LOG_TAG + "filtered gateways", filteredGateways);

    return filteredGateways;
  });

  // ------------------
  // ---- gateways
  // -----------------

  private gatewaysLastFetch: Date;
  private gatewaysRefreshDelay: number = 600; // 10 mins

  private readonly gatewaysLoading = signal<boolean>(false);
  readonly gatewaysLoading$ = this.gatewaysLoading.asReadonly();
  private readonly gateways = signal<Gateway[]>([]);
  private tourGateways: Gateway[] = [];

  private readonly gatewaysById = signal<Record<string, Gateway>>({});
  private tourGatewaysById = signal<Record<string, Gateway>>({});

  readonly gateways$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourGateways : this.gateways();
  });

  fetchGateways(forcedRefresh: boolean = false) {
    try {
      if (this.gatewaysLoading()) return;

      if (
        forcedRefresh ||
        !this.gatewaysLastFetch ||
        this.utilsService.calculateElapsedSeconds(this.gatewaysLastFetch) >
          this.gatewaysRefreshDelay
      ) {
        this.gatewaysLoading.set(true);
        console.log("MIGRATION / fetchGateways...");

        this.gatewayService.getGatewaysForStore().subscribe({
          next: (gateways: Gateway[]) => {
            this.gateways.set(gateways);
            this.gatewaysById.set(this._computeIndexedGateways(gateways));
            if (this.screenMode !== GatewayScreenMode.Loading) {
              this.gatewaysLoading.set(false);
            }
            this.gatewaysLastFetch = new Date();
          },
          error: (err) => {
            console.error("failed to fetch gateways : ", err);
            this.error.set(err.message || "An error occurred.");
            this.gatewaysLoading.set(false);
          },
        });
      }
    } catch (error) {
      console.error("ERROR ON FETCH GATEWAYS", error);
    }
  }

  public getLabel(gatewayUuid: string): string {
    const gatewaysById = this.gatewaysById$();

    let label = null;

    if (gatewaysById && Object.keys(gatewaysById).length > 0) {
      const gateway = gatewaysById[gatewayUuid];

      if (gateway && gateway.universalLabel) {
        label = gateway.universalLabel;
      }
    }

    return label;
  }

  public clear() {
    this.gatewaysLastFetch = null;

    this.gatewaysLoading.set(false);
    this.gateways.set([]);

    this.forcedRefresh.set(false);
  }

  readonly gatewaysById$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourGatewaysById() : this.gatewaysById();
  });

  readonly gatewayUuids$ = computed(() => {
    const gatewaysById = this.gatewaysById$();
    return Object.keys(gatewaysById);
  });

  readonly gatewaysCount$ = computed(() => {
    const gatewaysUuids = this.gatewayUuids$();

    return gatewaysUuids.length;
  });

  // ---------------------------
  // --- LIFE CYCLE METHODS
  // ---------------------------

  public constructor() {
    this._prepareTourData();
    effect(
      () => {
        const forcedRefresh = this.forcedRefresh();
        if (forcedRefresh === true) {
          this.fetchGateways(forcedRefresh);
          this.forcedRefresh.set(false);
        }
      },
      { allowSignalWrites: true }
    );
  }

  public _computeIndexedGateways(gateways: Gateway[]): Record<string, Gateway> {
    const index: Record<string, Gateway> = {};

    gateways.forEach((gateway) => {
      index[gateway.gateway_uuid] = gateway;
    });

    return index;
  }

  // --------------------
  // ---- PRIVATE METHODS
  // --------------------

  private _prepareTestGateways(): void {
    const gatewayLoraNoPlace = this._createGateway(
      "00005813d3093eb1",
      DateTime.local().minus({ month: 3 }),
      DateTime.local().minus({ minutes: 3 }),
      GatewayType.LoRaGateway,
      "Femto_Lite-4324R-Greet"
    );
    const gatewayLinkioNoPlace = this._createGateway(
      "008b4fcc92ca122baa8659b584d180b1",
      DateTime.local().minus({ year: 1 }),
      DateTime.local().minus({ days: 31 }),
      GatewayType.LinkioGateway,
      "LinkioGateway-noPlace"
    );
    const gatewayLoraParis1 = this._createGateway(
      "00005813d3093eb2",
      DateTime.local().minus({ month: 2 }),
      DateTime.local().minus({ days: 17 }),
      GatewayType.LoRaGateway,
      "Femto_Lite-345454-Greet"
    );
    const gatewayLoraParis2 = this._createGateway(
      "00005813d3093eb3",
      DateTime.local().minus({ hour: 2 }),
      DateTime.local().minus({ minutes: 3 }),
      GatewayType.LoRaGateway,
      "MerryloT_Hub-A29C"
    );
    const gatewayLinkioParis = this._createGateway(
      "008b4fcc92ca122baa8659b584d180b2",
      DateTime.local().minus({ month: 3 }),
      DateTime.local().minus({ minutes: 1 }),
      GatewayType.LinkioGateway,
      "LinkioGateway-Paris"
    );
    const gatewayLoraNoDevice = this._createGateway(
      "00005813d3093eb4",
      DateTime.local().minus({ month: 3 }),
      DateTime.local().minus({ hour: 3 }),
      GatewayType.LoRaGateway,
      "piconext-1044df-CCAS"
    );
    const gatewayLinkioNoDevice = this._createGateway(
      "008b4fcc92ca122baa8659b584d180b3",
      DateTime.local().minus({ month: 8 }),
      DateTime.local().minus({ second: 49 }),
      GatewayType.LinkioGateway,
      "LinkioGateway-NoDevice"
    );
    const gatewayLinkioGrenoble = this._createGateway(
      "008b4fcc92ca122baa8659b584d180b4",
      DateTime.local().minus({ minutes: 13 }),
      DateTime.local().minus({ minutes: 1 }),
      GatewayType.LinkioGateway,
      "LinkioGateway-Grenoble"
    );
    const gatewayLoraNewYork = this._createGateway(
      "00005813d3093eb5",
      DateTime.local().minus({ month: 3 }),
      DateTime.local().minus({ minutes: 3 }),
      GatewayType.LoRaGateway,
      "Femto_Lite-782VS-Rodez"
    );

    this.tourGateways.push(gatewayLoraNoPlace);
    this.tourGateways.push(gatewayLinkioNoPlace);
    this.tourGateways.push(gatewayLoraParis1);
    this.tourGateways.push(gatewayLoraParis2);
    this.tourGateways.push(gatewayLinkioParis);
    this.tourGateways.push(gatewayLoraNoDevice);
    this.tourGateways.push(gatewayLinkioNoDevice);
    this.tourGateways.push(gatewayLinkioGrenoble);
    this.tourGateways.push(gatewayLoraNewYork);
  }

  private _prepareTourData() {
    switch (this.appStore.gatewaysScenario) {
      case GatewayTourScenario.None:
        // nothing to do
        break;
      case GatewayTourScenario.One:
        const oneGateway = this._createGateway(
          "008b4fcc92ca122baa8659b584d180b6",
          DateTime.local().minus({ month: 3 }),
          DateTime.local().minus({ minutes: 3 }),
          GatewayType.LinkioGateway,
          "LinkioGateway"
        );
        this.tourGateways.push(oneGateway);
        break;
      case GatewayTourScenario.Full:
        const firstGateway = this._createGateway(
          "008b4fcc92ca122baa8659b584d180b7",
          DateTime.local().minus({ month: 3 }),
          DateTime.local().minus({ minutes: 3 }),
          GatewayType.LinkioGateway,
          "LinkioGateway_Paris"
        );
        const secondGateway = this._createGateway(
          "008b4fcc92ca122baa8659b584d180b8",
          DateTime.local().minus({ month: 1 }),
          DateTime.local().minus({ minutes: 50 }),
          GatewayType.LinkioGateway,
          "LinkioGateway_Grenoble"
        );
        const thirdGateway = this._createGateway(
          "00005813d3093eb7",
          DateTime.local().minus({ year: 1 }),
          null,
          GatewayType.LoRaGateway,
          "LoRaGateway"
        );
        this.tourGateways.push(firstGateway);
        this.tourGateways.push(secondGateway);
        this.tourGateways.push(thirdGateway);
        break;
      case GatewayTourScenario.Test:
        this._prepareTestGateways();
        break;
    }

    this.tourGatewaysById.set(this._computeIndexedGateways(this.tourGateways));
  }

  private _createGateway(
    uuid: string,
    last_seen: DateTime,
    first_seen: DateTime,
    type: GatewayType,
    device: string
  ): Gateway {
    return new Gateway(uuid, last_seen, first_seen, type, device, null);
  }
}
